import React, { useState, useEffect, useCallback } from "react";
import { formatTier, tierKey } from "../lib/tierLabel";
import { useAuth } from "../context/AuthContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import { ScrollArea } from "./ui/scroll-area";
import { toast } from "sonner";
import {
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  Truck,
  Server,
  Users,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Layers,
  Link2,
  Unlink,
  Sparkles,
  RefreshCw,
  ChevronRight,
  Target,
  Shield,
  ShieldCheck,
  Cloud,
  Globe,
  Zap,
} from "lucide-react";

const TIER_CONFIG = {
  tier_1: { label: "Critical", color: "bg-red-100 text-red-700 border-red-200", icon: "🔴" },
  tier_2: { label: "Important", color: "bg-amber-100 text-amber-700 border-amber-200", icon: "🟡" },
  tier_3: { label: "Standard", color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: "🟢" },
};

const TYPE_ICONS = {
  process: <Server className="w-4 h-4" />,
  vendor: <Truck className="w-4 h-4" />,
  facility: <Building2 className="w-4 h-4" />,
  it_system: <Server className="w-4 h-4" />,
  people: <Users className="w-4 h-4" />,
  equipment: <Zap className="w-4 h-4" />,
  data: <Layers className="w-4 h-4" />,
};

export default function DependencyDetailPanel({
  isOpen,
  onClose,
  selectedItem,
  itemType = "process", // "process" or "asset"
}) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dependencyData, setDependencyData] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const fetchDependencyDetails = useCallback(async () => {
    if (!selectedItem?.id) return;
    
    setLoading(true);
    try {
      // Fetch process-centered dependency graph
      const response = await api.get(`/dependencies/graph/process/${selectedItem.id}`);
      setDependencyData(response.data);
    } catch (error) {
      console.error("Error fetching dependency details:", error);
      // If process endpoint fails, try to build from asset data
      if (selectedItem) {
        setDependencyData({
          nodes: [],
          edges: [],
          summary: {
            center_process: selectedItem.asset_name || selectedItem.process_name,
            upstream_count: selectedItem.upstream_dependencies?.length || 0,
            downstream_count: selectedItem.downstream_dependencies?.length || 0,
          }
        });
      }
    } finally {
      setLoading(false);
    }
  }, [api, selectedItem]);

  const fetchAiSuggestions = useCallback(async () => {
    if (!selectedItem?.id) return;
    
    setLoadingSuggestions(true);
    try {
      const response = await api.get(`/dependencies/suggestions/${selectedItem.id}`);
      setAiSuggestions(response.data.suggestions || []);
    } catch (error) {
      console.error("Error fetching AI suggestions:", error);
      setAiSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [api, selectedItem]);

  useEffect(() => {
    if (isOpen && selectedItem) {
      fetchDependencyDetails();
      fetchAiSuggestions();
    }
  }, [isOpen, selectedItem, fetchDependencyDetails, fetchAiSuggestions]);

  const getTierBadge = (tier) => {
    const config = TIER_CONFIG[tier] || TIER_CONFIG.tier_3;
    return (
      <Badge variant="outline" className={config.color}>
        {config.icon} {config.label}
      </Badge>
    );
  };

  const renderDependencyList = (items, direction, emptyMessage) => {
    if (!items || items.length === 0) {
      return (
        <div className="text-center py-6 text-gray-400">
          <Unlink className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {items.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors"
            data-testid={`dependency-item-${direction}-${idx}`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                direction === "upstream" ? "bg-blue-50 text-blue-600" : "bg-purple-50 text-purple-600"
              }`}>
                {TYPE_ICONS[item.nodeType] || TYPE_ICONS.process}
              </div>
              <div>
                <p className="font-medium text-gray-900">{item.label || item.name}</p>
                <p className="text-xs text-gray-500">
                  {item.nodeType?.replace("_", " ").toUpperCase() || "Process"}
                  {item.department && ` • ${item.department}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {item.tier && getTierBadge(item.tier)}
              {item.rto_hours && (
                <Badge variant="secondary" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {item.rto_hours}h RTO
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderVendorDependencies = (vendors) => {
    if (!vendors || vendors.length === 0) return null;
    
    return (
      <div className="space-y-2">
        {vendors.map((vendor, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-3 rounded-lg border bg-emerald-50/50 hover:bg-emerald-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
                <Truck className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{vendor.label || vendor.vendor_name}</p>
                <p className="text-xs text-gray-500">
                  {vendor.vendor_type || "Vendor"}
                  {vendor.criticality && ` • ${vendor.criticality.toUpperCase()}`}
                </p>
              </div>
            </div>
            {vendor.effective_rto && (
              <Badge variant="outline" className="border-emerald-200 text-emerald-700">
                RTO: {vendor.effective_rto}h
              </Badge>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderFacilityDependencies = (facilities) => {
    if (!facilities || facilities.length === 0) return null;
    
    return (
      <div className="space-y-2">
        {facilities.map((facility, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-3 rounded-lg border bg-amber-50/50 hover:bg-amber-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 text-amber-600">
                <Building2 className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{facility.label || facility.facility_name}</p>
                <p className="text-xs text-gray-500">
                  {facility.facility_type || "Facility"}
                  {facility.city && ` • ${facility.city}`}
                </p>
              </div>
            </div>
            {facility.effective_rto && (
              <Badge variant="outline" className="border-amber-200 text-amber-700">
                RTO: {facility.effective_rto}h
              </Badge>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Extract dependencies from graph data
  const getUpstreamProcesses = () => {
    if (!dependencyData?.nodes) return [];
    return dependencyData.nodes.filter(n => 
      n.data?.nodeType === "process" && 
      !n.data?.isCenter &&
      dependencyData.edges?.some(e => e.target === selectedItem?.id && e.source === n.id)
    ).map(n => ({ ...n.data, id: n.id }));
  };

  const getDownstreamProcesses = () => {
    if (!dependencyData?.nodes) return [];
    return dependencyData.nodes.filter(n => 
      n.data?.nodeType === "process" && 
      !n.data?.isCenter &&
      dependencyData.edges?.some(e => e.source === selectedItem?.id && e.target === n.id)
    ).map(n => ({ ...n.data, id: n.id }));
  };

  const getVendors = () => {
    if (!dependencyData?.nodes) return [];
    return dependencyData.nodes.filter(n => n.data?.nodeType === "vendor").map(n => n.data);
  };

  const getFacilities = () => {
    if (!dependencyData?.nodes) return [];
    return dependencyData.nodes.filter(n => n.data?.nodeType === "facility").map(n => n.data);
  };

  // ---- Hosted Infrastructure (B1/B2 read-only, reuses graph/topology data) ----
  const getNodeById = (id) => dependencyData?.nodes?.find(n => n.id === id);

  const getHostedApplications = () => {
    if (!dependencyData?.nodes) return [];
    const apps = dependencyData.nodes
      .filter(n => n.data?.nodeType === "application")
      .map(n => ({ ...n.data, id: n.id }));
    const locEdges = (dependencyData?.edges || []).filter(
      e => e.data?.edge_kind === "located_at"
    );
    return apps.map(app => {
      const edge = locEdges.find(e => e.source === app.id);
      let host = null;
      if (edge) {
        const target = getNodeById(edge.target);
        if (target) {
          host = {
            id: target.id,
            label: target.data?.label || target.data?.infra_class,
            type: target.data?.nodeType, // "infrastructure" | "facility"
            infraClass: target.data?.infra_class,
            severityColor: target.data?.infra_severity_color,
            tier: target.data?.most_critical_tier,
          };
        }
      }
      return { ...app, host };
    });
  };

  const HOST_ICON = {
    Datacenter: <Server className="w-4 h-4 text-violet-600" />,
    Cloud: <Cloud className="w-4 h-4 text-sky-600" />,
    SaaS: <Globe className="w-4 h-4 text-teal-600" />,
    "Vendor Site": <Building2 className="w-4 h-4 text-orange-600" />,
  };

  // Blast-radius one-liner (Task 2): concentration summary derived from panel rows.
  const _tierN = (t) => {
    const m = String(t == null ? "" : t).match(/(\d)/);
    return m ? parseInt(m[1], 10) : null;
  };
  const _tierLabel = (t) => {
    const n = _tierN(t);
    return n ? `Tier-${n}` : null;
  };
  const getBlastRadius = () => {
    const apps = getHostedApplications();
    if (apps.length === 0) return null;
    const sensitive = apps.filter((a) => a.sensitive).length;
    const byHost = {};
    let unmapped = 0;
    apps.forEach((a) => {
      if (!a.host) { unmapped += 1; return; }
      const key = a.host.label || "Unknown";
      if (!byHost[key]) byHost[key] = { count: 0, tier: a.host.tier, label: key };
      byHost[key].count += 1;
      if (a.host.tier && (!byHost[key].tier || _tierN(a.host.tier) < _tierN(byHost[key].tier))) {
        byHost[key].tier = a.host.tier;
      }
    });
    const parts = Object.values(byHost).map(
      (h) => `${h.count} ${h.tier ? _tierLabel(h.tier) + " " : ""}${h.label}`
    );
    if (unmapped) parts.push(`${unmapped} unmapped`);
    return { sensitive, text: parts.join(" · ") };
  };

  const renderHostedInfrastructure = (apps) => {
    if (!apps || apps.length === 0) {
      return (
        <div className="text-center py-6 text-gray-400" data-testid="hosted-infrastructure-empty">
          <Server className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No hosted applications mapped for this process</p>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {apps.map((app, idx) => (
          <div
            key={app.id || idx}
            data-testid={`hosted-app-${idx}`}
            className="p-3 rounded-lg border bg-violet-50/40"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-violet-100 text-violet-600">
                  <Server className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{app.label}</p>
                  <p className="text-xs text-gray-500">
                    {app.deployment_type ? String(app.deployment_type).toUpperCase() : "Application"}
                    {app.criticality && ` • ${String(app.criticality).toUpperCase()}`}
                  </p>
                </div>
              </div>
              {app.sensitive && (
                <Badge variant="outline" className="border-rose-200 text-rose-700 text-xs">
                  <Shield className="w-3 h-3 mr-1" /> Sensitive
                </Badge>
              )}
            </div>

            {/* Hosting target + B2 severity color */}
            <div className="mt-2 flex items-center gap-2 text-sm">
              <ChevronRight className="w-3 h-3 text-gray-400" />
              {app.host ? (
                <span className="flex items-center gap-1.5 flex-wrap">
                  {HOST_ICON[app.host.infraClass] || <Building2 className="w-4 h-4 text-gray-500" />}
                  <span className="text-gray-700">
                    Hosted at <strong>{app.host.label}</strong>
                  </span>
                  {app.host.severityColor && (
                    <span
                      title={`Most critical hosted tier: ${app.host.tier || "n/a"}`}
                      data-testid={`infra-severity-${idx}`}
                      className="inline-block w-3 h-3 rounded-full border border-white shadow"
                      style={{ backgroundColor: app.host.severityColor }}
                    />
                  )}
                  {app.host.tier && (
                    <Badge variant="secondary" className="text-[10px]">
                      {formatTier(app.host.tier).toUpperCase()}
                    </Badge>
                  )}
                </span>
              ) : (
                <span className="text-gray-400">Hosting not mapped</span>
              )}
            </div>

            {/* RPO + protection hint (sensitive apps, where present) */}
            {app.sensitive && (app.rpo_hours != null || app.protection_hint || app.security_attributes) && (
              <div className="mt-2 space-y-1" data-testid={`hosted-protection-${idx}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {app.rpo_hours != null && (
                    <Badge variant="outline" className="text-xs border-violet-200 text-violet-700">
                      <Clock className="w-3 h-3 mr-1" /> RPO: {app.rpo_hours}h
                    </Badge>
                  )}
                  {app.protection_hint && (
                    <span className="text-xs text-gray-600 flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3 text-emerald-500" />
                      {app.protection_hint}
                    </span>
                  )}
                </div>
                {app.security_attributes && (
                  <div className="text-[11px] text-gray-400 flex items-center gap-1" data-testid={`hosted-security-${idx}`}>
                    <span className="font-medium">Security:</span> {app.security_attributes}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const itemName = selectedItem?.asset_name || selectedItem?.process_name || selectedItem?.label || "Unknown";
  const itemTier = selectedItem?.tier || selectedItem?.data?.tier;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[500px] sm:w-[600px] sm:max-w-[600px] p-0" data-testid="dependency-detail-panel">
        <SheetHeader className="p-6 pb-4 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-start justify-between">
            <div>
              <SheetTitle className="text-xl flex items-center gap-2">
                <Target className="w-5 h-5 text-sky-700" />
                {itemName}
              </SheetTitle>
              <SheetDescription className="mt-1">
                Dependency Analysis & Impact Chain
              </SheetDescription>
            </div>
            {itemTier && getTierBadge(itemTier)}
          </div>
          
          {/* Quick Stats */}
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-2 text-sm">
              <ArrowUpRight className="w-4 h-4 text-blue-500" />
              <span className="text-gray-600">
                <strong>{dependencyData?.summary?.upstream_count || 0}</strong> Upstream
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <ArrowDownRight className="w-4 h-4 text-purple-500" />
              <span className="text-gray-600">
                <strong>{dependencyData?.summary?.downstream_count || 0}</strong> Downstream
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Truck className="w-4 h-4 text-emerald-500" />
              <span className="text-gray-600">
                <strong>{dependencyData?.summary?.vendor_count || getVendors().length}</strong> Vendors
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="w-4 h-4 text-amber-500" />
              <span className="text-gray-600">
                <strong>{dependencyData?.summary?.facility_count || getFacilities().length}</strong> Facilities
              </span>
            </div>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-200px)]">
          <div className="p-6 space-y-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
                <span className="ml-2 text-gray-500">Loading dependencies...</span>
              </div>
            ) : (
              <>
                {/* Upstream Dependencies */}
                <Card className="border-blue-100">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-blue-700">
                      <ArrowUpRight className="w-5 h-5" />
                      Upstream Dependencies
                      <Badge variant="secondary" className="ml-auto">{getUpstreamProcesses().length}</Badge>
                    </CardTitle>
                    <p className="text-xs text-gray-500">Processes and systems this depends on</p>
                  </CardHeader>
                  <CardContent>
                    {renderDependencyList(
                      getUpstreamProcesses(),
                      "upstream",
                      "No upstream dependencies identified"
                    )}
                  </CardContent>
                </Card>

                {/* Downstream Dependencies */}
                <Card className="border-purple-100">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-purple-700">
                      <ArrowDownRight className="w-5 h-5" />
                      Downstream Dependencies
                      <Badge variant="secondary" className="ml-auto">{getDownstreamProcesses().length}</Badge>
                    </CardTitle>
                    <p className="text-xs text-gray-500">Processes and systems that depend on this</p>
                  </CardHeader>
                  <CardContent>
                    {renderDependencyList(
                      getDownstreamProcesses(),
                      "downstream",
                      "No downstream dependencies identified"
                    )}
                  </CardContent>
                </Card>

                {/* Vendor Dependencies */}
                {getVendors().length > 0 && (
                  <Card className="border-emerald-100">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
                        <Truck className="w-5 h-5" />
                        Vendor Dependencies
                        <Badge variant="secondary" className="ml-auto">{getVendors().length}</Badge>
                      </CardTitle>
                      <p className="text-xs text-gray-500">Third-party suppliers and service providers</p>
                    </CardHeader>
                    <CardContent>
                      {renderVendorDependencies(getVendors())}
                    </CardContent>
                  </Card>
                )}

                {/* Facility Dependencies */}
                {getFacilities().length > 0 && (
                  <Card className="border-amber-100">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                        <Building2 className="w-5 h-5" />
                        Facility Dependencies
                        <Badge variant="secondary" className="ml-auto">{getFacilities().length}</Badge>
                      </CardTitle>
                      <p className="text-xs text-gray-500">Physical locations and infrastructure</p>
                    </CardHeader>
                    <CardContent>
                      {renderFacilityDependencies(getFacilities())}
                    </CardContent>
                  </Card>
                )}

                {/* Hosted Infrastructure (B1/B2 — read-only) */}
                <Card className="border-violet-100" data-testid="hosted-infrastructure-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-violet-700">
                      <Server className="w-5 h-5" />
                      Hosted Infrastructure
                      <Badge variant="secondary" className="ml-auto">{getHostedApplications().length}</Badge>
                    </CardTitle>
                    <p className="text-xs text-gray-500">Where each application runs and how it's protected</p>
                  </CardHeader>
                  <CardContent>
                    {(() => { const br = getBlastRadius(); return br ? (
                      <div className="mb-3 px-3 py-2 rounded-md bg-violet-50 border border-violet-100 text-xs text-violet-800 font-medium" data-testid="blast-radius-summary">
                        {br.sensitive} sensitive app{br.sensitive === 1 ? "" : "s"} on {br.text}
                      </div>
                    ) : null; })()}
                    {renderHostedInfrastructure(getHostedApplications())}
                  </CardContent>
                </Card>

                {/* AI Suggestions */}
                <Card className="border-sky-200 bg-gradient-to-br from-indigo-50/50 to-purple-50/50">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-sky-700">
                      <Sparkles className="w-5 h-5" />
                      AI Dependency Suggestions
                      {loadingSuggestions && <RefreshCw className="w-4 h-4 animate-spin ml-2" />}
                    </CardTitle>
                    <p className="text-xs text-gray-500">Potential missing dependencies identified by AI</p>
                  </CardHeader>
                  <CardContent>
                    {aiSuggestions.length > 0 ? (
                      <div className="space-y-2">
                        {aiSuggestions.map((suggestion, idx) => (
                          <div
                            key={idx}
                            className="p-3 rounded-lg border border-sky-100 bg-white"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className={
                                  suggestion.type === "upstream" 
                                    ? "border-blue-200 text-blue-700" 
                                    : "border-purple-200 text-purple-700"
                                }>
                                  {suggestion.type === "upstream" ? (
                                    <><ArrowUpRight className="w-3 h-3 mr-1" /> Upstream</>
                                  ) : (
                                    <><ArrowDownRight className="w-3 h-3 mr-1" /> Downstream</>
                                  )}
                                </Badge>
                                <span className="font-medium text-gray-900">{suggestion.name}</span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600 mt-2">{suggestion.reason}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-400">
                        <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                        <p className="text-sm">No missing dependencies detected</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Impact Warning */}
                {itemTier === "tier_1" && (
                  <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="pt-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                        <div>
                          <p className="font-medium text-red-700">Critical Asset Warning</p>
                          <p className="text-sm text-red-600 mt-1">
                            This is a Criticality Tier 1 (Critical) asset. Any disruption may have significant 
                            cascading effects on {getDownstreamProcesses().length} downstream processes.
                            Ensure all dependencies have documented recovery procedures.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
